import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TMachineMaintenanceState } from '../reducers';
import {
  TEmployee,
  TFilterSelected,
  TListingFetchReason,
  TREQUEST_DELETE_EMPLOYEE,
  TREQUEST_UPDATE_EMPLOYEE,
  TRESPONSE_EMPLOYEE_LIST,
  TTicketId,
} from '@monorepo/types';
import * as EmployeeManagementActions from './employee-management.actions';
import * as EmployeeManagementSelector from './employee-management.selectors';
import { TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';

@Injectable({
  providedIn: 'root',
})
export class EmployeeManagementFacade {
  constructor(
    private store: Store<TMachineMaintenanceState>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private mmDataService: MachineMaintenanceDataService,
  ) {
    this.initializePagination();
  }

  initializePagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('employeeListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('employeeListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('employeeListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new EmployeeManagementActions.SetEmployeeListPagination(pagination));
  }

  resetEmployeeListInfo(fetchReason: TListingFetchReason) {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('employeeListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setPagination({ pageNo: 1, pageSize });
    this.fetchEmployeeListInfo([], fetchReason);
  }

  fetchEmployeeListInfo(selectedFilters: TFilterSelected[] = [], fetchReason: TListingFetchReason) {
    this.store.dispatch(new EmployeeManagementActions.GetEmployeeListInfo(selectedFilters, fetchReason));
  }

  fetchDeleteEmployeeReplacements(
    employeeId: TRESPONSE_EMPLOYEE_LIST['employeesWithAssignments'][number]['employee']['employeeId'],
  ) {
    this.store.dispatch(new EmployeeManagementActions.DeleteEmployeeReplacements(employeeId));
  }

  async getEmployeeReplacementsForMaintenanceTicket() {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getEmployeeReplacementsForMaintenanceTickets().pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async getEmployeeReplacementsForMachineIssueTicket(ticketId: TTicketId) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getEmployeeReplacementsForMachineIssueTicket({ ticketId }).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  deleteEmployeeListRow(payload: Omit<TREQUEST_DELETE_EMPLOYEE, 'factoryId'>) {
    this.store.dispatch(new EmployeeManagementActions.DeleteEmployee(payload));
  }

  updateEmployeeListRow(updatedEmployee: TREQUEST_UPDATE_EMPLOYEE) {
    return this.store.dispatch(new EmployeeManagementActions.UpdateEmployee(updatedEmployee));
  }

  async getAssignableShiftsForEmployee(employeeId: TEmployee['employeeId']) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.getAssignableShiftsForEmployee({ employeeId }).pipe(
        map((res) => res.shifts),
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  updateEmployeeManagementPageNo(payload: number) {
    this.store.dispatch(new EmployeeManagementActions.UpdateEmployeeManagementPageNo(payload));
  }

  getEmployeeListInfo() {
    return this.store.pipe(select(EmployeeManagementSelector.getEmployeeListInfo));
  }

  getDeleteEmployeeReplacements() {
    return this.store.pipe(select(EmployeeManagementSelector.getDeleteEmployeeReplacements));
  }

  getPagination() {
    return this.store.pipe(select(EmployeeManagementSelector.getPagination));
  }

  async fetchIssueCategoriesAndMachineType() {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.mmDataService.fetchIssueCategoriesAndMachineType().pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetEmployeeListInfo = ReturnType<EmployeeManagementFacade['getEmployeeListInfo']>;
export type TGetDeleteEmployeeReplacements = ReturnType<EmployeeManagementFacade['getDeleteEmployeeReplacements']>;
export type TGetEmployeeListPagination = ReturnType<EmployeeManagementFacade['getPagination']>;
